export type LedgerType =
  | "BANK_ACCOUNT"
  | "CREDIT_LINE"
  | "CREDIT_ACCOUNT"
  | "PAYMENT_GATEWAY";

export const LEDGER_TYPES: LedgerType[] = [
  "BANK_ACCOUNT",
  "CREDIT_LINE",
  "CREDIT_ACCOUNT",
  "PAYMENT_GATEWAY",
];

export const LEDGER_TYPE_LABEL: Record<LedgerType, string> = {
  BANK_ACCOUNT: "Bank account",
  CREDIT_LINE: "Credit line",
  CREDIT_ACCOUNT: "Credit account",
  PAYMENT_GATEWAY: "Payment gateway",
};

export type LedgerNormalBalance = "DEBIT" | "CREDIT";

export type LedgerAccountCategory =
  | "BANK_ACCOUNT"
  | "ACCOUNTS_RECEIVABLE"
  | "REVENUE"
  | "VAT_LIABILITY"
  | "ACCOUNTS_PAYABLE"
  | "EXPENSE"
  | "CUSTOMER_RECEIPTS"
  | "CARD_PROCESSING_FEES"
  | "PAYMENT_CLEARING"
  | "SUPPLIER_PAYMENT_CLEARING"
  | "CUSTOMER_CREDIT_LIABILITY"
  | "OTHER";

export type LedgerBoundEntityType =
  | "ORGANIZATION"
  | "BRAND"
  | "SUPPLIER"
  | "CUSTOMER_PAYMENT_METHOD"
  | "SUPPLIER_PAYMENT_METHOD";

export const LEDGER_ACCOUNT_CATEGORY_LABEL: Record<
  LedgerAccountCategory,
  string
> = {
  BANK_ACCOUNT: "Bank account",
  ACCOUNTS_RECEIVABLE: "Accounts receivable",
  REVENUE: "Sales revenue",
  VAT_LIABILITY: "VAT liability",
  ACCOUNTS_PAYABLE: "Accounts payable",
  EXPENSE: "Expense",
  CUSTOMER_RECEIPTS: "Customer receipts",
  CARD_PROCESSING_FEES: "Card processing fees",
  PAYMENT_CLEARING: "Payment clearing",
  SUPPLIER_PAYMENT_CLEARING: "Supplier payment clearing",
  CUSTOMER_CREDIT_LIABILITY: "Customer credit vouchers",
  OTHER: "Other",
};

const LEDGER_NORMAL_BALANCE_BY_TYPE: Record<LedgerType, LedgerNormalBalance> = {
  BANK_ACCOUNT: "DEBIT",
  CREDIT_LINE: "CREDIT",
  CREDIT_ACCOUNT: "CREDIT",
  PAYMENT_GATEWAY: "DEBIT",
};

const LEDGER_NORMAL_BALANCE_BY_CATEGORY: Record<
  LedgerAccountCategory,
  LedgerNormalBalance
> = {
  BANK_ACCOUNT: "DEBIT",
  ACCOUNTS_RECEIVABLE: "DEBIT",
  REVENUE: "CREDIT",
  VAT_LIABILITY: "CREDIT",
  ACCOUNTS_PAYABLE: "CREDIT",
  EXPENSE: "DEBIT",
  CUSTOMER_RECEIPTS: "DEBIT",
  CARD_PROCESSING_FEES: "DEBIT",
  PAYMENT_CLEARING: "CREDIT",
  SUPPLIER_PAYMENT_CLEARING: "CREDIT",
  CUSTOMER_CREDIT_LIABILITY: "CREDIT",
  OTHER: "DEBIT",
};

export function normalBalanceForLedgerType(type: LedgerType): LedgerNormalBalance {
  return LEDGER_NORMAL_BALANCE_BY_TYPE[type];
}

export function normalBalanceForAccountCategory(
  category: LedgerAccountCategory
): LedgerNormalBalance {
  return LEDGER_NORMAL_BALANCE_BY_CATEGORY[category];
}

export function resolveLedgerNormalBalance(args: {
  type: LedgerType;
  accountCategory?: LedgerAccountCategory;
}): LedgerNormalBalance {
  if (args.accountCategory) {
    return normalBalanceForAccountCategory(args.accountCategory);
  }
  return normalBalanceForLedgerType(args.type);
}

export function normalizeLedgerCode(raw: unknown): string | undefined {
  if (raw === undefined || raw === null) {
    return undefined;
  }
  const trimmed = String(raw).trim();
  return trimmed || undefined;
}

export interface iLedger {
  id?: string;
  organizationId: string;
  /** Optional short account code (e.g. 1100). Unique per organization when set. */
  code?: string;
  name: string;
  currency: string;
  openingBalance: number;
  type: LedgerType;
  normalBalance: LedgerNormalBalance;
  accountCategory?: LedgerAccountCategory;
  isSystemDefault?: boolean;
  boundEntityType?: LedgerBoundEntityType;
  boundEntityId?: string;
  archived: boolean;
  createdAt: number;
  createdBy: string;
  updatedAt?: number;
  updatedBy?: string;
}
